import React, { useState, useMemo } from 'react';
import { TrendCompare } from '../components/SharedComponents';
import { mergeSeasons } from './paceMerge';

const ACCENT = '#1d6a5f';
const SEASON_LABEL = {
  'vinter-2526': 'Vinter 25/26', 'vinter-2425': 'Vinter 24/25',
  'sommar-2026': 'Sommar 2026', 'sommar-2025': 'Sommar 2025',
};
const PRIOR = { 'vinter-2526': 'vinter-2425', 'sommar-2026': 'sommar-2025' };

// ── datum-hjälp ──────────────────────────────────────────────────
const parseISO = (s) => new Date(s + 'T12:00:00Z');
function isoMonday(y, w) {
  const jan4 = new Date(Date.UTC(y, 0, 4));
  const dow = (jan4.getUTCDay() + 6) % 7;
  const wk1 = new Date(jan4); wk1.setUTCDate(jan4.getUTCDate() - dow);
  const d = new Date(wk1); d.setUTCDate(wk1.getUTCDate() + (w - 1) * 7);
  return d;
}
function seasonYears(season) {
  const m = season.match(/(\d{2})(\d{2})$/);            // vinter-2526 → 25,26
  if (season.startsWith('vinter')) return { y1: 2000 + +m[1], y2: 2000 + +m[2], typ: 'vinter' };
  const y = parseInt(season.match(/(\d{4})$/)[1], 10);  // sommar-2026
  return { y, typ: 'sommar' };
}
function weekYear(season, week) {
  const sy = seasonYears(season);
  if (sy.typ === 'vinter') return week >= 30 ? sy.y1 : sy.y2;
  return sy.y;
}
const targetDate = (season, week) => isoMonday(weekYear(season, week), week);
const leadWeeks = (season, week, asOf) =>
  Math.round((targetDate(season, week) - parseISO(asOf)) / (7 * 86400000));

export default function PaceView({ paceLive }) {
  const PH = useMemo(() => mergeSeasons(paceLive), [paceLive]);
  const seasons = Object.keys(PH);
  // default: säsong med senaste snapshot (= aktuell)
  const defSeason = useMemo(() => {
    let best = seasons[0], bestD = '';
    for (const s of seasons) { const mx = (PH[s].snapshots || []).slice(-1)[0] || ''; if (mx > bestD) { bestD = mx; best = s; } }
    return best;
  }, [PH]); // eslint-disable-line
  const [season, setSeason] = useState(defSeason);

  const sData = PH[season] || { rows: [] };
  const weeks = useMemo(() => {
    const ws = [...new Set(sData.rows.map(r => r.week))];
    return ws.sort((a, b) => targetDate(season, a) - targetDate(season, b));
  }, [season]); // eslint-disable-line

  // default vecka: första kommande (targetDate >= idag), annars sista
  const [week, setWeek] = useState(null);
  const curWeek = week ?? (() => {
    const today = new Date(); const up = weeks.find(w => targetDate(season, w) >= today);
    return up ?? weeks[weeks.length - 1];
  })();

  const priorSeason = PRIOR[season];

  // Bygg pace per ledtid (veckor före målveckan)
  const byLead = (seas) => {
    if (!seas || !PH[seas]) return {};
    const rows = PH[seas].rows.filter(r => r.week === curWeek);
    const m = {};
    for (const r of rows) { if (r.bel == null) continue; m[leadWeeks(seas, curWeek, r.asOf)] = r; }
    return m;
  };
  const cur = byLead(season);
  const prev = priorSeason ? byLead(priorSeason) : {};
  const nearestPrev = (L) => {
    let best = null, bd = 99;
    for (const k of Object.keys(prev)) { const d = Math.abs(+k - L); if (d < bd) { bd = d; best = prev[k]; } }
    return bd <= 2 ? best : null;
  };

  const leads = [...new Set([...Object.keys(cur), ...Object.keys(prev)].map(Number))]
    .filter(L => L >= -1 && L <= 30).sort((a, b) => b - a); // långt → nära
  const chartData = leads.map(L => ({
    label: L > 0 ? `${L}v` : (L === 0 ? 'Veckan' : 'efter'),
    value: cur[L] ? cur[L].bel : null,
    prev: (() => { const p = nearestPrev(L); return p ? p.bel : null; })(),
  }));

  // KPI: senaste läget i år vs fjol vid samma ledtid
  const curLeads = Object.keys(cur).map(Number).sort((a, b) => a - b);
  const latestL = curLeads.length ? curLeads[0] : null; // minsta ledtid = senaste avstämning
  const curNow = latestL != null ? cur[latestL] : null;
  const prevSame = latestL != null ? nearestPrev(latestL) : null;
  const prevFinal = (() => { const ls = Object.keys(prev).map(Number).sort((a, b) => a - b); return ls.length ? prev[ls[0]] : null; })();
  const deltaPp = (curNow && prevSame) ? Math.round(curNow.bel - prevSame.bel) : null;

  const chip = (active, label, onClick) => (
    <button onClick={onClick} style={{
      fontSize: 12.5, fontWeight: 600, padding: '6px 13px', borderRadius: 999,
      border: '1px solid ' + (active ? ACCENT : 'var(--border)'),
      background: active ? ACCENT : 'transparent', color: active ? '#fff' : 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap',
    }}>{label}</button>
  );
  const card = (label, value, sub, color) => (
    <div style={{ background: 'var(--surface-2,#faf9f6)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || 'var(--text)', marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ fontFamily: 'system-ui,sans-serif' }}>
      <div className="card">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>Säsong</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {seasons.map(s => chip(season === s, SEASON_LABEL[s] || s, () => { setSeason(s); setWeek(null); }))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>Målvecka</div>
            <select value={curWeek} onChange={e => setWeek(+e.target.value)}
              style={{ fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)' }}>
              {weeks.map(w => <option key={w} value={w}>Vecka {w}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 12 }}>
        {card('Bokat nu', curNow ? Math.round(curNow.bel) + '%' : '–', curNow ? `${curNow.rumsn} rumsnätter` : '', ACCENT)}
        {card('Mot fjol (samma ledtid)', deltaPp != null ? `${deltaPp >= 0 ? '+' : ''}${deltaPp} pp` : '–',
          prevSame ? `i fjol ${Math.round(prevSame.bel)}% då` : 'ingen fjoldata', deltaPp == null ? undefined : deltaPp >= 0 ? '#1D9E75' : '#A32D2D')}
        {card('Fjolårets slutliga', prevFinal ? Math.round(prevFinal.bel) + '%' : '–', prevFinal ? `${prevFinal.rumsn} rumsnätter` : '')}
      </div>

      <div className="card">
        <div className="card-title">Bokningstakt v.{curWeek} — beläggning per ledtid (veckor före)</div>
        <TrendCompare data={chartData} formatVal={(v) => Math.round(v) + '%'} curColor={ACCENT} prevColor="#C2A878" />
        <div className="note" style={{ marginTop: 6 }}>
          Heldragen = {SEASON_LABEL[season]}, streckad = {priorSeason ? SEASON_LABEL[priorSeason] : 'fjolår saknas'}.
          X-axeln är veckor före veckans start (vänster = långt före, höger = inpå). Källa: manuell veckoavstämning + automatiska snapshots framåt.
        </div>
      </div>
    </div>
  );
}
